import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * Line icons traced from `Templates.dc.html`. The design draws each one as an
 * inline stroked SVG rather than a Material glyph, so the shapes live here
 * instead of pulling near-matches from `@mui/icons-material`.
 *
 * All of them inherit `currentColor`; size comes from the caller's `sx`.
 */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Folder tile in grid view — the design draws it at stroke 1.7. */
export const FolderGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.7}
      d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
    />
  </SvgIcon>
);

/** Folder tile in list view — slightly heavier stroke at the smaller size. */
export const FolderSmallGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.8}
      d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
    />
  </SvgIcon>
);

/** Document tile in grid view — folded corner plus two text rules. */
export const DocumentGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.7}
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
    />
    <path {...strokeProps} strokeWidth={1.7} d="M14 3v5h5" />
    <path {...strokeProps} strokeWidth={1.7} d="M9 13h6M9 17h4" />
  </SvgIcon>
);

/** Document tile in list view — the design drops the text rules here. */
export const DocumentSmallGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.8}
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
    />
    <path {...strokeProps} strokeWidth={1.8} d="M14 3v5h5" />
  </SvgIcon>
);

/** "Create" — plus in a circle. */
export const PlusCircleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2.2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2.2} d="M12 8v8M8 12h8" />
  </SvgIcon>
);

/** Bare plus — the floating "Create" pill on the narrow phone column. */
export const PlusGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.3} d="M12 5v14M5 12h14" />
  </SvgIcon>
);

/** Back arrow shown in the app bar while a folder is open on small screens. */
export const ArrowLeftGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M19 12H5M12 19l-7-7 7-7" />
  </SvgIcon>
);

export const SearchGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="11" cy="11" r="7" />
    <path {...strokeProps} strokeWidth={2} d="m21 21-4.3-4.3" />
  </SvgIcon>
);

/** Grid half of the view toggle. */
export const GridViewGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="3"
      y="3"
      width="7"
      height="7"
      rx="1.5"
    />
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="14"
      y="3"
      width="7"
      height="7"
      rx="1.5"
    />
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="14"
      y="14"
      width="7"
      height="7"
      rx="1.5"
    />
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="3"
      y="14"
      width="7"
      height="7"
      rx="1.5"
    />
  </SvgIcon>
);

/** List half of the view toggle. */
export const ListViewGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
    />
  </SvgIcon>
);

/** Breadcrumb separator. */
export const ChevronRightGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M9 6l6 6-6 6" />
  </SvgIcon>
);

export const HomeGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M3 10.5 12 3l9 7.5" />
    <path {...strokeProps} strokeWidth={2} d="M5 9.5V21h14V9.5" />
  </SvgIcon>
);

/** Vertical three-dot overflow trigger — filled, not stroked. */
export const KebabGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </SvgIcon>
);

/** Split-page mark used by the sidebar entry and the "no templates" state. */
export const TemplatesGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={1.8}
      x="3"
      y="4"
      width="18"
      height="16"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={1.8} d="M3 9h18M9 9v11" />
  </SvgIcon>
);
